import { useEffect, useState } from 'react';

import { DateRange } from 'react-day-picker';

import { ExtendedDateRange, type FormikProps, ScheduleTimeFrame } from 'common/contracts';
import { OfferType } from 'constants/enums';
import dayjs from 'dayjs';
import { getDurationTextFromMinutes } from 'utils/formattingUtil';
import ScheduleAppearancesTable from 'views/Offers/components/ScheduleAppearancesTable';
import { SCHEDULE_FIELD_NAME } from 'views/Offers/Promotions/Form/PromotionsForm.consts';

import Button from 'components/Button/Button';
import DatePicker from 'components/DatePicker/DatePicker';
import { DatePickerMode, DatePickerValue } from 'components/DatePicker/DatePicker.types';
import Input from 'components/Input/Input';
import { Label } from 'components/ui/label';

interface ScheduleProps {
    formikProps: FormikProps;
    editRangeDatesValues: ExtendedDateRange | null;
    setEditRangeDatesValues: React.Dispatch<React.SetStateAction<ExtendedDateRange | null>>;
    appearancesTableData: ScheduleTimeFrame[];
    resetScheduleValues: () => void;
}

const Schedule: React.FC<ScheduleProps> = ({
    formikProps,
    editRangeDatesValues,
    setEditRangeDatesValues,
    appearancesTableData,
    resetScheduleValues
}) => {
    const { values, handleChange, handleBlur, touched, errors, setFieldValue } =
        formikProps;

    const [localDateRange, setLocalDateRange] = useState<DateRange>({
        from: undefined,
        to: undefined
    });
    const [localNotes, setLocalNotes] = useState('');
    const [formattedDuration, setFormattedDuration] = useState<string | null>(null);

    const clearLocalDateRange = () => {
        setLocalDateRange({ from: undefined, to: undefined });
    };

    useEffect(() => {
        if (localDateRange.from && localDateRange.to) {
            const diffMinutes = dayjs(localDateRange.to).diff(
                dayjs(localDateRange.from),
                'minute'
            );
            const duration = getDurationTextFromMinutes(diffMinutes);
            setFormattedDuration(duration);
        } else {
            setFormattedDuration(null);
        }
    }, [localDateRange]);

    useEffect(() => {
        if (editRangeDatesValues) {
            const [startDate, endDate, notes] = editRangeDatesValues;

            setLocalDateRange({
                from: startDate,
                to: endDate
            });
            setLocalNotes(notes || '');
        } else {
            // Clear form when editRangeDatesValues is cleared
            clearLocalDateRange();
            setLocalNotes('');
        }
    }, [editRangeDatesValues]);

    const handleApply = () => {
        const fromUtcDate = localDateRange.from;
        const toUtcDate = localDateRange.to;

        if (fromUtcDate && toUtcDate) {
            const newTimeFrame: ScheduleTimeFrame = {
                startTime: new Date(fromUtcDate.toISOString()),
                endTime: new Date(toUtcDate.toISOString()),
                notes: localNotes
            };

            if (editRangeDatesValues) {
                const [editStartTime, editEndTime] = editRangeDatesValues;

                const editStartTimestamp = editStartTime.getTime();
                const editEndTimestamp = editEndTime.getTime();

                const updatedTimeFrames = values?.schedule?.timeFrames.map(
                    (timeFrame: ScheduleTimeFrame) => {
                        const currentStartTimestamp = new Date(timeFrame.startTime).getTime();
                        const currentEndTimestamp = new Date(timeFrame.endTime).getTime();

                        return currentStartTimestamp === editStartTimestamp &&
                            currentEndTimestamp === editEndTimestamp
                            ? newTimeFrame
                            : timeFrame;
                    }
                );
                setFieldValue(SCHEDULE_FIELD_NAME, {
                    permanent: false,
                    timeFrames: updatedTimeFrames
                });
            } else {
                setFieldValue(SCHEDULE_FIELD_NAME, {
                    permanent: false,
                    timeFrames: [...values?.schedule?.timeFrames, newTimeFrame]
                });
            }

            // Reset local state
            clearLocalDateRange();
            setLocalNotes('');
            setEditRangeDatesValues(null);
            resetScheduleValues();
        } else {
            console.error('Invalid date range provided: Start or End date is null.');
        }
    };

    const handleCancel = () => {
        clearLocalDateRange();
        setLocalNotes('');
        setEditRangeDatesValues(null);
        resetScheduleValues();
    };

    const handleEditTimeframe = (
        startTime: Date | string,
        endTime: Date | string,
        notes: string
    ) => {
        const start = startTime instanceof Date ? startTime : new Date(startTime);
        const end = endTime instanceof Date ? endTime : new Date(endTime);
        setEditRangeDatesValues([start, end, notes]);
    };

    const handleAvailabilityChange = (e: React.ChangeEvent<HTMLInputElement>) => handleChange(e);

    const handleDateRangeApply = (value: DatePickerValue) => setLocalDateRange(value as DateRange);

    const handleNotesChange = (e: React.ChangeEvent<HTMLInputElement>) => setLocalNotes(e.target.value);

    const availabilityError = touched.playerAvailability && errors.playerAvailability ? 'error' : 'default';

    const availabilityDescription = touched.playerAvailability && errors.playerAvailability ? errors.playerAvailability : '';

    const isApplyDisabled = !localDateRange.from || !localDateRange.to || (touched.playerAvailability && Boolean(errors.playerAvailability));

    const buttonLabel = editRangeDatesValues ? 'Apply' : 'Add Appearance';

    return (
        <div className="space-y-9">
            <div className="w-full">
                <div className="flex gap-4">
                    {values.type === OfferType.SPECIAL_OFFER && (
                        <div className="space-y-2 w-[20%]">
                            <Input
                                label="Availability"
                                id="availability-scheduled"
                                name="playerAvailability"
                                type="number"
                                placeholder="1"
                                min={0}
                                value={values.playerAvailability || ''}
                                onChange={handleAvailabilityChange}
                                onBlur={handleBlur}
                                state={availabilityError}
                                description={availabilityDescription}
                                isRequired={values.setAsFree}
                            />
                        </div>
                    )}
                    <div className="flex-1">
                        <DatePicker
                            label="Pick a date"
                            labelSpacing="mb-2"
                            shouldDisplayTime={true}
                            shouldExcludePast={true}
                            mode={DatePickerMode.RANGE}
                            value={localDateRange}
                            onApply={handleDateRangeApply}
                        />
                    </div>
                </div>
                {formattedDuration && (
                    <div className="space-y-2 mt-5">
                        <Label className="text-sm text-zinc-900 font-medium">Duration</Label>
                        <Input
                            value={formattedDuration}
                            disabled
                        />
                    </div>
                )}
                <div className="space-y-2 mt-5">
                    <Input
                        label="Notes"
                        name="notes"
                        value={localNotes}
                        placeholder="Type your note here"
                        onChange={handleNotesChange}
                    />
                </div>
                <div className="space-y-3 mt-6">
                    {editRangeDatesValues && (
                        <Button
                            disabled={!localDateRange.from || !localDateRange.to}
                            onClick={handleCancel}
                            className="w-full bg-base-accent text-base-accentForeground hover:bg-button-hover-create"
                        >
                            Cancel
                        </Button>
                    )}
                    <Button
                        disabled={isApplyDisabled}
                        onClick={handleApply}
                        className="w-full bg-base-accent text-base-accentForeground"
                        isCreateButton
                    >
                        {buttonLabel}
                    </Button>
                </div>
            </div>

            {appearancesTableData.length > 0 && (
                <div className="w-full">
                    <ScheduleAppearancesTable
                        formikProps={formikProps}
                        appearancesTableData={appearancesTableData}
                        onEdit={handleEditTimeframe}
                        onDelete={resetScheduleValues}
                    />
                </div>
            )}
        </div>
    );
};

export default Schedule;
